"use client";

import CloseIcon from "@repo/ui-system/icons/action/close/outline.svg";
import { Dialog } from "radix-ui";

import type { BestProduct } from "@/lib/api/best/keywords/schema";
import { BiEvent } from "@/lib/bi/bi.enum";
import { getStoreLogo } from "@/lib/store-logos";
import { cn } from "@/lib/utils";

import { formatReviewCount } from "../../reviews/format-review-count";
import { formatPercentOff } from "../format-percent-off";
import { isDisplayableDiscount } from "../store-deal";
import {
  StorePromotionPopupContent,
  type PopupSecondaryStore,
} from "./store-promotion-popup-content";
import { usePromotionOpen } from "./use-promotion-open";

type Props = {
  product: BestProduct;
  lpv: string;
  delayMs?: number;
  className?: string;
};

/**
 * Store-specific promotion popup (COM-681).
 *
 * Duplicated from PromotionPopup; the original is left untouched. Differs by
 * showing the primary store prominently with its own discount + deal, and the
 * remaining stores under an "Also at:" row. The discount tag in the header is
 * sourced from the primary store (not the product-level percentOff).
 */
export function StorePromotionPopup({
  product,
  lpv,
  delayMs = 15_000,
  className,
}: Props) {
  const { isOpen, setIsOpen } = usePromotionOpen({ delayMs, thresholdPx: 60 });

  const { stores } = product;
  const primaryStore = stores[0];
  const primaryDiscount =
    primaryStore && "discount" in primaryStore ? primaryStore.discount : null;
  // Same minimum-discount rule as the store rows.
  const primaryPercentOff = isDisplayableDiscount(primaryDiscount)
    ? primaryDiscount.percentage
    : null;

  const secondaryStores: PopupSecondaryStore[] = stores
    .slice(1)
    .map((store) => ({
      name: store.name,
      logo: getStoreLogo(store),
      url: store.url,
      bi: {
        eventType: BiEvent.BannerOutbound as const,
        id: product.id,
        provider: store.provider ?? store.name,
        seller: store.name,
        lpv,
        location: "promotion-popup",
      },
    }));

  const content = {
    badge: product.mainBadge,
    discountLabel: formatPercentOff(primaryPercentOff),
    imageUrl: product.images[0]?.url ?? "",
    title: product.name,
    reviewCount: formatReviewCount(product.reviews),
    ctaLabel: "Check price",
    primaryStore: {
      name: primaryStore.name,
      logo: getStoreLogo(primaryStore),
      url: primaryStore.url,
      bi: {
        eventType: BiEvent.BannerOutbound as const,
        id: product.id,
        provider: primaryStore.provider ?? primaryStore.name,
        seller: primaryStore.name,
        lpv,
        location: "promotion-popup",
      },
      deal: primaryStore.deal ?? null,
    },
    secondaryStores,
  };

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={setIsOpen}
    >
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            "fixed inset-0 z-modal bg-[rgba(21,20,20,0.65)]",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            className,
          )}
        />
        <Dialog.Content
          onOpenAutoFocus={(e) => e.preventDefault()}
          className={cn(
            "fixed left-1/2 top-1/2 z-modal -translate-x-1/2 -translate-y-1/2",
            "w-[calc(100%-40px)] max-w-[320px] lg:max-w-[540px] rounded-2xl shadow-xl",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
            "duration-200",
            "rounded-2xl overflow-hidden",
          )}
        >
          <Dialog.Description className="sr-only">
            A limited-time promotional offer for {content.title} from{" "}
            {content.primaryStore.name}.
          </Dialog.Description>

          <Dialog.Close className="absolute top-3 right-3 lg:top-5 lg:right-5 z-20 flex items-center justify-center size-[26px] rounded-full lg:hover:bg-black/10 lg:transition-colors">
            <CloseIcon
              className="size-5 text-[#1a1c23]"
              aria-hidden="true"
            />
            <span className="sr-only">Close</span>
          </Dialog.Close>

          <StorePromotionPopupContent content={content} />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
